import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaStethoscope, FaFlask, FaBox, FaXRay } from "react-icons/fa";
import { getMyBookings } from "../api/bookings.api";
import { getStaffId } from "../utils/storage";

import EmptyState from "./States/EmptyState";
import LoadingState from "./States/LoadingState";

const diagnosticKind = (b) => {
  const items = b.cartItems || [];
  const hasXray = items.some((i) => i.type === "xray");
  const hasTest = items.some((i) => i.type === "test");
  if (hasXray && !hasTest) return "X-Ray/Scan";
  if (hasTest && !hasXray) return "Lab Test";
  return "Diagnostic";
};

const diagnosticTitleFallback = (b) => {
  const kind = diagnosticKind(b);
  if (b.serviceType === "Home Collection") return `${kind} (Home Collection)`;
  if (b.serviceType === "Center Visit") return `${kind} (Center Visit)`;
  return `${kind} Consultation`;
};

const bookingLabel = (b) => {
  if (b.package?.name) return b.package.name;
  const doctorName = b.doctorId?.name || b.doctor?.name;
  if (doctorName) return `Dr. ${doctorName}`;
  if (b.testName) return b.testName;
  if (b.cartItems?.length) {
    const first = (b.cartItems[0].title || b.cartItems[0].name || "").trim();
    if (first) {
      const extra = b.cartItems.length - 1;
      return extra > 0 ? `${first} +${extra} more` : first;
    }
    return diagnosticTitleFallback(b);
  }
  if (b.diagnostic?.name) return b.diagnostic.name;
  if (b.serviceType) return b.serviceType;
  return "Booking";
};

const bookingType = (b) => {
  if (b.package?._id) return { icon: FaBox, label: "Package", color: "text-teal-600" };
  if (b.doctorConsultationBookingId || b.doctorId) return { icon: FaStethoscope, label: "Doctor", color: "text-primary" };
  const hasXray = b.cartItems?.some((i) => i.type === "xray");
  if (hasXray) return { icon: FaXRay, label: "Scan", color: "text-orange-500" };
  return { icon: FaFlask, label: "Lab", color: "text-primary" };
};

const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const RecentBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const staffId = getStaffId();

  useEffect(() => {
    if (!staffId) {
      setLoading(false);
      return;
    }
    getMyBookings(staffId)
      .then((response) => {
        if (response.data.success && response.data.bookings) {
          setBookings(response.data.bookings.slice().reverse().slice(0, 3));
        }
      })
      .catch((err) => console.error("Recent bookings error:", err))
      .finally(() => setLoading(false));
  }, [staffId]);

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-on-primary-fixed-var">Recent Bookings</h3>
        <Link to="/mybookings" className="text-sm font-medium text-primary hover:underline">
          View All
        </Link>
      </div>

      {loading ? (
        <LoadingState branded brandedCompact message="Loading…" className="py-4" />
      ) : bookings.length === 0 ? (
        <p className="text-sm text-on-surface-variant">No recent bookings yet.</p>
      ) : (
        <div className="space-y-2">
          {bookings.map((booking) => {
            const { icon: Icon, label, color } = bookingType(booking);
            const status = booking.status || "Confirmed";
            const isCancelled = status === "Cancelled";
            return (
              <div
                key={booking._id || booking.bookingId}
                className="flex items-center justify-between gap-3 p-3 rounded-xl bg-surface-low border border-surface-container/50"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-surface-lowest flex items-center justify-center shrink-0">
                    <Icon className={color} size={16} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-on-primary-fixed-var text-sm truncate">{bookingLabel(booking)}</p>
                    <p className="text-xs text-on-surface-variant truncate">
                      {[formatDate(booking.date), booking.timeSlot, label].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                </div>
                <span className={`shrink-0 text-[10px] font-semibold px-2 py-1 rounded-full ${
                  isCancelled ? "bg-error-container text-error" : "bg-primary-fixed/30 text-primary"
                }`}>
                  {status}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RecentBookings;
